declare global {
    interface Window {
        trackDocumentEmbedView: (container: HTMLElement) => void;
        getDeviceInfo?: () => { isMobile: boolean; isTablet: boolean };
    }
}

const ENDPOINT = '/api/document-view';

interface TrackingPayload {
    document_drawer_id?: number;
    doc_ref?: string;
    source_table: string;
    source_id: number;
    display_mode: string;
    page_id: number | null;
    nav_level: number | null;
    is_mobile: boolean;
}

function getPageContext(): { pageId: number | null; navLevel: number | null } {
    const pageIdMeta = document.querySelector<HTMLMetaElement>(
        'meta[name="page-id"]',
    );
    const navLevelMeta = document.querySelector<HTMLMetaElement>(
        'meta[name="nav-level"]',
    );

    return {
        pageId: pageIdMeta ? parseInt(pageIdMeta.content, 10) || null : null,
        navLevel: navLevelMeta
            ? parseInt(navLevelMeta.content, 10) || null
            : null,
    };
}

function isMobileDevice(): boolean {
    const info = window.getDeviceInfo?.();
    if (info) {
        return info.isMobile || info.isTablet;
    }
    return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
}

function sendTrackingEvent(data: TrackingPayload): void {
    try {
        const payload = JSON.stringify(data);
        if (navigator.sendBeacon) {
            const blob = new Blob([payload], { type: 'application/json' });
            navigator.sendBeacon(ENDPOINT, blob);
        } else {
            fetch(ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: payload,
                keepalive: true,
            });
        }
    } catch {
        // Tracking failures are silent — never block the user.
    }
}

function extractFilenameFromHref(href: string): string | null {
    try {
        const url = new URL(href, window.location.origin);
        const segments = url.pathname.split('/');
        return segments[segments.length - 1] || null;
    } catch {
        return null;
    }
}

function buildEventData(
    element: HTMLElement,
    displayMode: string,
): TrackingPayload {
    const context = getPageContext();

    return {
        document_drawer_id: parseInt(
            element.getAttribute('data-drawer-id') ?? '0',
            10,
        ),
        source_table: element.getAttribute('data-source-table') ?? '',
        source_id:
            parseInt(element.getAttribute('data-source-id') ?? '0', 10) || 0,
        display_mode: displayMode,
        page_id: context.pageId,
        nav_level: context.navLevel,
        is_mobile: isMobileDevice(),
    };
}

function buildDocRefEventData(
    docRef: string,
    displayMode: string,
): TrackingPayload {
    const context = getPageContext();

    return {
        doc_ref: docRef,
        source_table: 'documents',
        source_id: 0,
        display_mode: displayMode,
        page_id: context.pageId,
        nav_level: context.navLevel,
        is_mobile: isMobileDevice(),
    };
}

function handleLinkClick(event: Event): void {
    const link = event.currentTarget as HTMLElement;
    if (!link.hasAttribute('data-drawer-id')) {
        return;
    }
    const data = buildEventData(link, 'link_click');
    sendTrackingEvent(data);
}

function handlePdfLinkClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const link = target.closest<HTMLAnchorElement>('a[href]');
    if (!link) {
        return;
    }
    if (link.hasAttribute('data-drawer-id')) {
        return;
    }
    const href = link.getAttribute('href') ?? '';
    if (!/\.pdf$/i.test(href.split('?')[0].split('#')[0])) {
        return;
    }
    const filename = extractFilenameFromHref(link.href);
    if (!filename) {
        return;
    }
    const data = buildDocRefEventData(filename, 'link_click');
    sendTrackingEvent(data);
}

function attachClickListeners(): void {
    const links = document.querySelectorAll<HTMLElement>('a[data-drawer-id]');
    links.forEach((link) => {
        link.addEventListener('click', handleLinkClick);
    });

    document.addEventListener('click', handlePdfLinkClick as EventListener);
}

function trackEmbedView(container: HTMLElement): void {
    if (!container?.hasAttribute('data-drawer-id')) {
        return;
    }
    const data = buildEventData(container, 'embed_view');
    sendTrackingEvent(data);
}

function init(): void {
    attachClickListeners();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}

window.trackDocumentEmbedView = trackEmbedView;

export {};
